import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import axios from "axios";
import requests from "@/services/requests";
import { IonicVue } from "@ionic/vue";
import VWave from "v-wave";

/* Core CSS required for Ionic components to work properly */
import "@ionic/vue/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/vue/css/normalize.css";
import "@ionic/vue/css/structure.css";
import "@ionic/vue/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/vue/css/padding.css";
import "@ionic/vue/css/float-elements.css";
import "@ionic/vue/css/text-alignment.css";
import "@ionic/vue/css/text-transformation.css";
import "@ionic/vue/css/flex-utils.css";
import "@ionic/vue/css/display.css";
import { defineCustomElements } from "@ionic/pwa-elements/loader";
import FooterSave from "@/components/FooterSave.vue";
import mdiVue from "mdi-vue/v3";
import * as mdijs from "@mdi/js";
import toast from "@/services/toast.js";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import { RecycleScroller } from "vue-virtual-scroller";
import fuzzy_date from "@/plugins/fuzzy_date.js";
import CreatedAt from "@/components/CreatedAt.vue";
import convert from "@/services/convert.js";
// import { SplashScreen } from "@capacitor/splash-screen";
import { Device } from "@capacitor/device";
import VueShepherdPlugin from "vue-shepherd";
require("shepherd.js/dist/css/shepherd.css");
import { VueScreenSizeMixin } from "vue-screen-size";
import * as Sentry from "@sentry/vue";
import InfoCard from "./components/InfoCard.vue";
import "@capacitor-community/camera-preview";
import SearchBar from "@/components/SearchBar.vue";

/* Import the new sync system */
import { syncApi } from "@/services/index";

/* Import clear sync notification utility for debugging */
import { clearSyncNotification } from "@/services/clearSyncNotification";

/* Theme variables */
import "./theme/variables.css";

import store from "./store";

// Initialize store listeners first
store.dispatch("initializeListeners");

// Initialize the new sync system
syncApi.init();

import {
    IonLoading,
    IonButton,
    IonBackButton,
    IonImg,
    IonCard,
    IonPage,
    IonContent,
    IonButtons,
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonIcon,
    IonCardContent,
    IonCardHeader,
    IonFabButton,
    IonFab,
    IonSelect,
    IonRow,
    IonCol,
    IonGrid,
    IonBadge,
    IonLabel,
    IonCheckbox,
    IonRippleEffect,
    IonInput,
    IonItemDivider,
    IonSelectOption,
    IonTextarea,
    IonToggle,
    IonThumbnail,
    IonListHeader,
    IonList,
    IonItem,
    IonRefresher,
    IonRefresherContent,
    IonText,
    IonSpinner,
    IonFooter,
    IonDatetime,
    IonDatetimeButton,
    IonChip,
    IonMenuToggle,
    IonItemOption,
    IonItemGroup,
    IonBreadcrumb,
    IonBreadcrumbs,
    IonItemOptions,
    IonItemSliding,
    IonRange,
} from "@ionic/vue";

axios.defaults.validateStatus = function (status) {
    if (status === 503) {
        console.error("503 Service Unavailable: Queue will stop.");
        requests.queue_stopped = true;
        return false;
    }
    return status >= 200 && status < 500;
};

// Configure axios interceptors globally (for all services using axios)
axios.interceptors.request.use(
    (config) => {
        // Ensure headers object exists
        config.headers = config.headers || {};

        // Try to get token from Vuex store first
        let token = store.getters.getBearer;

        // Fallback to localStorage if Vuex hasn't been hydrated yet
        if (!token) {
            token = localStorage.getItem('bearer');

            if (token) {
                console.warn('[Auth] Token retrieved from localStorage fallback. Vuex store may not be hydrated yet.');
            }
        }

        if (token) {
            // Set authorization and content headers
            config.headers.Authorization = `Bearer ${token}`;
            config.headers['Content-Type'] = 'application/vnd.api+json';
            config.headers.Accept = 'application/vnd.api+json';
        } else {
            console.warn('[Auth] No bearer token available for request:', config.url);

            if (config.headers.Authorization) {
                delete config.headers.Authorization;
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

axios.interceptors.response.use(
    (response) => {
        if (response.status >= 200 && response.status < 300) {
            return response;
        } else {
            return Promise.reject(response);
        }
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            // Log detailed information about the 401 error
            console.error('[Auth] 401 Unauthorized received:', {
                url: error.config?.url,
                hasAuthHeader: !!error.config?.headers?.Authorization,
                storeToken: !!store.getters.getBearer,
                localStorageToken: !!localStorage.getItem('bearer')
            });

            router.push("/login");
        }
        return Promise.reject(error);
    }
);

// Verify VuexPersistence has restored bearer token
const verifyAuthState = () => {
    const storeToken = store.getters.getBearer;
    const localToken = localStorage.getItem('bearer');

    if (localToken && !storeToken) {
        console.warn('[Auth Init] Token exists in localStorage but not in Vuex store. Manually restoring...');
        store.dispatch('setBearer', localToken);
    }

    console.log('[Auth Init] Authentication state verified:', {
        storeHasToken: !!storeToken,
        localStorageHasToken: !!localToken,
        tokensMatch: storeToken === localToken
    });
};

// Call after a short delay to allow VuexPersistence to restore
setTimeout(verifyAuthState, 100);

async function getDeviceInformation() {
    try {
        const deviceInfo = await Device.getInfo();
        return deviceInfo;
    } catch (error) {
        console.error("An error occurred while getting device information:", error);
        return null;
    }
}

getDeviceInformation().then((device) => {
    const app = createApp(App)
        .use(defineCustomElements)
        .use(store)
        .use(IonicVue, {
            mode: "ios",
            animated: true,
            swipeBackEnabled: false,
        })
        .use(router);

    app.component("RecycleScroller", RecycleScroller);
    app.component("CreatedAt", CreatedAt);
    app.use(VWave, {
        directive: "ripple",
        cancellationPeriod: 80,
        color: "#18B69B",
        opacity: 0.1,
        enterDuration: 350,
    });

    // Definieer een dynamische globale property voor $api_url
    Object.defineProperty(app.config.globalProperties, "$api_url", {
        get() {
            return store.getters.getApiUrl;
        }
    });

    app.config.globalProperties.$axios = axios;
    app.config.globalProperties.$requests = requests;
    app.config.globalProperties.$toast = toast;
    app.config.globalProperties.$fuzzyDate = fuzzy_date;
    app.config.globalProperties.$platform = device.platform;
    app.config.globalProperties.$convert = convert;

    app.use(mdiVue, {
        icons: mdijs,
    });

    app.mixin(VueScreenSizeMixin);
    app.use(VueShepherdPlugin);

    const decimalComma = {
        mounted(el, binding) {
            el.addEventListener("input", () => {
                let value = el.value;
                value = value.replace(".", ",");
                el.value = value.replace(/[^\d,]/g, "");
                binding.instance[binding.arg] = parseFloat(value.replace(",", "."));
            });
        },
    };

    // Sentry.init({
    //     app,
    //     dsn: "https://ff14d7a4104ba6c7f1f20f0fcb232e06@o4507644920528896.ingest.de.sentry.io/4507644946415696",
    //     integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    //     tracesSampleRate: 0.2,
    //     tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    //     replaysSessionSampleRate: 0.05,
    //     replaysOnErrorSampleRate: 1.0,
    // });

    app.directive("decimal-comma", decimalComma);

    app.component("IonLoading", IonLoading);
    app.component("IonButton", IonButton);
    app.component("IonImg", IonImg);
    app.component("IonBackButton", IonBackButton);
    app.component("IonButtons", IonButtons);
    app.component("IonCard", IonCard);
    app.component("IonPage", IonPage);
    app.component("IonContent", IonContent);
    app.component("IonModal", IonModal);
    app.component("IonHeader", IonHeader);
    app.component("IonToolbar", IonToolbar);
    app.component("IonTitle", IonTitle);
    app.component("IonIcon", IonIcon);
    app.component("IonCardContent", IonCardContent);
    app.component("IonCardHeader", IonCardHeader);
    app.component("IonFabButton", IonFabButton);
    app.component("IonFab", IonFab);
    app.component("IonSelect", IonSelect);
    app.component("IonCol", IonCol);
    app.component("IonRow", IonRow);
    app.component("IonGrid", IonGrid);
    app.component("IonBadge", IonBadge);
    app.component("IonLabel", IonLabel);
    app.component("IonCheckbox", IonCheckbox);
    app.component("IonRippleEffect", IonRippleEffect);
    app.component("IonInput", IonInput);
    app.component("IonItemDivider", IonItemDivider);
    app.component("IonSelectOption", IonSelectOption);
    app.component("IonTextarea", IonTextarea);
    app.component("IonToggle", IonToggle);
    app.component("IonThumbnail", IonThumbnail);
    app.component("IonListHeader", IonListHeader);
    app.component("IonList", IonList);
    app.component("IonItem", IonItem);
    app.component("IonRefresher", IonRefresher);
    app.component("IonRefresherContent", IonRefresherContent);
    app.component("IonText", IonText);
    app.component("IonSpinner", IonSpinner);
    app.component("IonFooter", IonFooter);
    app.component("IonDatetime", IonDatetime);
    app.component("IonDatetimeButton", IonDatetimeButton);
    app.component("IonChip", IonChip);
    app.component("IonMenuToggle", IonMenuToggle);
    app.component("IonItemOption", IonItemOption);
    app.component("IonItemGroup", IonItemGroup);
    app.component("IonBreadcrumb", IonBreadcrumb);
    app.component("IonBreadcrumbs", IonBreadcrumbs);
    app.component("IonItemOptions", IonItemOptions);
    app.component("IonItemSliding", IonItemSliding);
    app.component("IonRange", IonRange);
    app.component("InfoCard", InfoCard);
    app.component("SearchBar", SearchBar);
    app.component("FooterSave", FooterSave);

    router.isReady().then(() => {
        app.mount("#app");
        
        // Make clearSyncNotification available globally for debugging
        window.clearSyncNotification = clearSyncNotification;
    });
});
